/**
 * Lightweight, consent-aware error monitoring.
 *
 * Events are sent to the project's own `log-error` edge function (no external
 * monitoring vendor), and only after the user has accepted analytics consent.
 * Payloads are scrubbed: no emails, money amounts, free text the user typed,
 * or AI prompts ever leave the browser.
 */
import { RUNTIME_ENVIRONMENT } from '@/env/client';
import { supabase } from '@/integrations/supabase/client';
import {
  MAX_STACK_LENGTH,
  scrubContext,
  scrubRoute,
  scrubText,
} from './scrub';

export type ErrorFeature =
  | 'app'
  | 'auth'
  | 'signup'
  | 'transactions'
  | 'goals'
  | 'wishlist'
  | 'debt'
  | 'billing'
  | 'future-me'
  | 'settings'
  | 'unknown';

export interface CaptureOptions {
  feature: ErrorFeature;
  error: unknown;
  severity?: 'error' | 'warning';
  /** Only booleans, short strings and safe counters survive scrubbing. */
  context?: Record<string, unknown>;
}

const CONSENT_KEY = 'consent.analytics';
const MAX_EVENTS_PER_WINDOW = 10;
const WINDOW_MS = 60_000;
const DEDUPE_MS = 10_000;

let initialised = false;
let windowStart = 0;
let sentInWindow = 0;
const recentSignatures = new Map<string, number>();

export function hasMonitoringConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch {
    return false;
  }
}

function withinRateLimit(signature: string): boolean {
  const now = Date.now();

  const lastSeen = recentSignatures.get(signature);
  if (lastSeen && now - lastSeen < DEDUPE_MS) return false;
  recentSignatures.set(signature, now);
  for (const [key, ts] of recentSignatures) {
    if (now - ts > WINDOW_MS) recentSignatures.delete(key);
  }

  if (now - windowStart > WINDOW_MS) {
    windowStart = now;
    sentInWindow = 0;
  }
  if (sentInWindow >= MAX_EVENTS_PER_WINDOW) return false;
  sentInWindow += 1;
  return true;
}

function toErrorParts(error: unknown): { name: string; message: string; stack: string } {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: scrubText(error.message),
      stack: scrubText(error.stack ?? '', MAX_STACK_LENGTH),
    };
  }
  return { name: 'NonError', message: scrubText(error), stack: '' };
}

/** Send one scrubbed error event. Never throws and never blocks the UI. */
export async function captureError({
  feature,
  error,
  severity = 'error',
  context,
}: CaptureOptions): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    if (!hasMonitoringConsent()) return;

    const { name, message, stack } = toErrorParts(error);
    if (!message) return;
    if (!withinRateLimit(`${feature}:${name}:${message}`)) return;

    const { data } = await supabase.auth.getSession();

    await supabase.functions.invoke('log-error', {
      body: {
        source: 'frontend',
        environment: RUNTIME_ENVIRONMENT,
        feature,
        severity,
        name,
        message,
        stack,
        route: scrubRoute(window.location?.pathname),
        release: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : undefined,
        userId: data.session?.user?.id ?? undefined,
        context: scrubContext(context),
      },
    });
  } catch (sendError) {
    // Monitoring must never break the app.
    console.warn('[errorMonitor] failed to report error', sendError);
  }
}

/** Attach global handlers for uncaught exceptions and rejected promises. */
export function initErrorMonitoring(): () => void {
  if (initialised || typeof window === 'undefined') return () => {};
  initialised = true;

  const onError = (event: ErrorEvent) => {
    void captureError({ feature: 'app', error: event.error ?? event.message });
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    void captureError({ feature: 'app', error: event.reason });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    initialised = false;
  };
}

/** Test helper: clear rate-limit/dedupe state. */
export function __resetErrorMonitorState(): void {
  windowStart = 0;
  sentInWindow = 0;
  recentSignatures.clear();
  initialised = false;
}
